import { Dialog, DialogActions, DialogContent } from "@mui/material";
import ResourceUtils from "lib/ResourceUtils";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { ButtonCancel, ButtonConfirm } from "./buttons";
import { MprcContainer } from "./MprcContainer";
import { MprcDialogTitleClose } from "./MprcDialogTitleClose";
import { MprcTextFieldMultiLanguage } from "./MprcMultiLanguage";
import { ExtractErrorInformation, MprcRequestError } from "./MprcRequestErrorReport";

////////////////////////////////////////////////////////////////////////////

interface IProps {
    title: string;
    locales: LocaleInput[];
    placeholder?: string;
    currentValues?: LocaleInputCollection,
    maxLength?: number;
    acceptEmptyValue?: boolean;
    onClose: () => void;
    onConfirm: (value: LocaleInputCollection | undefined) => Promise<void>;
}

////////////////////////////////////////////////////////////////////////////

export const MprcSimpleEditMultiLanguageDialog: React.FC<IProps> = (props) => {

    const { title, placeholder, locales, maxLength, currentValues, acceptEmptyValue = false, onClose, onConfirm } = props;
    const { enqueueSnackbar } = useSnackbar();

    const [text, setText] = useState<LocaleInputCollection>(currentValues ?? ResourceUtils.buildEmptyLocaleInputCollection(locales.map(x => x.locale)));
    const [loading, setLoading] = useState<boolean>(false);

    const isRecordChange = props.currentValues != text;
    const inputError = text != undefined && acceptEmptyValue === false && text?.length === 0;
    ////////////////////////////////////////////////////////////////////////////

    ///
    const handleConfirm = async () => {
        setLoading(true);
        try {
            await onConfirm(text);
        }
        catch (e) {
            let error = ExtractErrorInformation(e as MprcRequestError);
            enqueueSnackbar(error.message, { variant: "error" });
        }
        finally {
            setLoading(false);
        }
    };

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return <Dialog
        open
        fullWidth
        maxWidth="sm"
        onClose={() => onClose()}
    >
        <MprcDialogTitleClose
            shrinkPadding
            title={title}
            onClose={() => onClose()}
        />

        <DialogContent>
            <MprcContainer margin="dense">
                <MprcTextFieldMultiLanguage
                    autoFocus
                    fullWidth
                    maxLength={maxLength}
                    margin="dense"
                    placeholder={placeholder}
                    currentValues={text}
                    locales={locales}
                    onLocaleInputChange={(v) => v && setText([...v])}
                    error={inputError}
                    disabled={loading}
                />
            </MprcContainer>
        </DialogContent>
        <DialogActions>
            <ButtonCancel disabled={loading} variant="contained" onClick={onClose} />
            <ButtonConfirm
                disabled={inputError || loading || !isRecordChange}
                variant="contained"
                onClick={() => handleConfirm()}
            />
        </DialogActions>
    </Dialog>;
};
